export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  content: string;
  keywords: string[];
}

export const blogPosts: BlogPost[] = [
  {
    id: '1',
    slug: 'angular-testing-library-guia-completa',
    title: 'Angular Testing Library: Guía Completa para Testing Moderno en Angular',
    description: 'Descubre cómo Angular Testing Library transforma cómo escribimos pruebas unitarias en Angular con un enfoque centrado en el usuario.',
    date: '2025-01-15',
    readTime: '5 min',
    keywords: ['angular testing library', 'testing library angular', '@testing-library/angular'],
    content: `Angular Testing Library se ha convertido en la herramienta de referencia para escribir pruebas unitarias en aplicaciones Angular. A diferencia de las utilidades tradicionales de Angular, esta librería adopta un enfoque centrado en el usuario que transforma cómo pensamos y escribimos nuestros tests.

## ¿Qué es Angular Testing Library?

Angular Testing Library es una adaptación de Testing Library específica para Angular. Su filosofía se basa en un principio simple pero poderoso: probar los componentes como lo haría un usuario real, no como lo haría un desarrollador que conoce la implementación interna.

La librería oficial \`@testing-library/angular\` proporciona utilidades que te permiten renderizar componentes, interactuar con ellos mediante queries semánticas y verificar comportamientos sin acoplar tus tests a detalles de implementación. Esto significa que puedes refactorizar tu código sin romper tus pruebas, siempre que la experiencia del usuario se mantenga igual.

## Ventajas sobre las utilidades tradicionales de Angular

Las utilidades de testing nativas de Angular (\`TestBed\`, \`ComponentFixture\`, \`DebugElement\`) te obligan a conocer la estructura interna del componente. Accedes a propiedades, métodos privados y detalles de implementación que no deberían importar a la hora de validar el comportamiento.

Angular Testing Library, en cambio, te fuerza a pensar en roles, textos accesibles y interacciones reales. Cuando escribes \`screen.getByRole('button', { name: 'Enviar' })\`, estás probando que existe un botón con ese texto visible, no que existe una propiedad \`submitButton\` en el componente.

Este cambio de mentalidad tiene consecuencias prácticas inmediatas:

- **Tests más resistentes**: No se rompen cuando cambias clases CSS, estructura HTML interna o nombres de variables
- **Mejor accesibilidad**: Al usar queries semánticas, descubres problemas de accesibilidad durante el desarrollo
- **Mantenibilidad**: Los tests son más fáciles de leer y entender, incluso para desarrolladores nuevos en el proyecto

## Configuración e integración con Jest

La integración de Angular Testing Library con Jest es directa. Una vez configurado Jest en tu proyecto Angular (reemplazando Karma y Jasmine), instalar \`@testing-library/angular\` es tan simple como:

\`\`\`bash
npm install --save-dev @testing-library/angular @testing-library/jest-dom
\`\`\`

La configuración básica requiere importar las utilidades necesarias y, opcionalmente, extender los matchers de Jest con \`@testing-library/jest-dom\` para tener aserciones más expresivas como \`toBeInTheDocument()\` o \`toHaveTextContent()\`.

## Ejemplo práctico: Testing de un componente con Signals

Con Angular moderno (versión 17+), los componentes standalone y Signals han cambiado cómo escribimos código. Angular Testing Library se adapta perfectamente a estos cambios:

\`\`\`typescript
import { render, screen } from '@testing-library/angular';
import { signal } from '@angular/core';
import { UserProfileComponent } from './user-profile.component';

describe('UserProfileComponent', () => {
  it('muestra el nombre del usuario cuando está disponible', async () => {
    const userName = signal('Juan Pérez');
    
    await render(UserProfileComponent, {
      componentInputs: {
        userName: userName()
      }
    });

    expect(screen.getByText('Juan Pérez')).toBeInTheDocument();
  });

  it('permite editar el perfil al hacer clic en el botón', async () => {
    const { user } = await render(UserProfileComponent);
    const editButton = screen.getByRole('button', { name: /editar/i });
    
    await user.click(editButton);
    
    expect(screen.getByRole('textbox', { name: /nombre/i })).toBeInTheDocument();
  });
});
\`\`\`

Este ejemplo muestra cómo Testing Library maneja Signals de forma natural y cómo el objeto \`user\` simula interacciones reales del usuario, no eventos sintéticos de bajo nivel.

## Testing de servicios y dependencias

Para servicios y lógica compleja, Angular Testing Library permite mockear dependencias de forma limpia mediante el parámetro \`providers\` en la función \`render\`. Esto mantiene el enfoque de testing de comportamiento sobre implementación, incluso cuando trabajas con servicios inyectados.

## Mejores prácticas y criterio técnico

El verdadero valor de Angular Testing Library no está solo en su API, sino en la disciplina que impone. Al escribir tests, debes preguntarte: "¿Qué ve y hace el usuario?" en lugar de "¿Cómo está implementado esto internamente?"

Esta mentalidad se extiende a cómo estructuras tus tests, qué verificas y qué ignoras. Un test bien escrito con Testing Library es un documento vivo de cómo funciona tu aplicación desde la perspectiva del usuario final.

## Recursos y aprendizaje continuo

Si buscas profundizar en Angular Testing Library y las mejores prácticas de testing en Angular moderno, en [angular-tests.dev](https://www.angular-tests.dev/) encontrarás guías completas, ejemplos prácticos y contenido actualizado sobre Jest, Testing Library y las últimas características de Angular. El enfoque está en proporcionar criterio técnico real, no solo tutoriales superficiales, para desarrolladores que construyen aplicaciones en producción.

La combinación de Angular Testing Library con Jest representa el estándar actual para testing en Angular. Adoptarla no es solo cambiar de herramienta, es adoptar una filosofía que mejora la calidad de tu código y la confianza en tus refactorizaciones.`
  },
  {
    id: '2',
    slug: 'test-unitarios-angular-fundamentos',
    title: 'Test Unitarios Angular: Fundamentos y Mejores Prácticas',
    description: 'Aprende los fundamentos de los test unitarios en Angular y las mejores prácticas para escribir pruebas efectivas y mantenibles.',
    date: '2025-01-14',
    readTime: '6 min',
    keywords: ['test unitarios angular', 'pruebas unitarias angular', 'test angular'],
    content: `Los test unitarios angular representan la base de una estrategia de testing sólida en aplicaciones Angular. A diferencia de otros tipos de pruebas, los tests unitarios validan componentes, servicios y funciones de forma aislada, proporcionando retroalimentación inmediata durante el desarrollo y confianza para refactorizar código.

## ¿Qué son los test unitarios en Angular?

Un test unitario en Angular verifica que una unidad de código (un componente, servicio, pipe o directiva) funciona correctamente de forma independiente. La clave está en la palabra "unidad": cada test debe ser independiente, rápido y predecible, sin depender de servicios externos reales, bases de datos o APIs.

En Angular, esto significa probar la lógica de negocio, las transformaciones de datos, las interacciones del usuario y el renderizado de componentes sin necesidad de montar toda la aplicación. Los tests unitarios se ejecutan en milisegundos, permitiendo ciclos de desarrollo rápidos y detección temprana de errores.

## Estructura de un test unitario en Angular

Un test unitario típico en Angular sigue la estructura Arrange-Act-Assert (AAA):

\`\`\`typescript
describe('UserService', () => {
  let service: UserService;
  let httpMock: HttpTestingController;

  beforeEach(() => {
    TestBed.configureTestingModule({
      imports: [HttpClientTestingModule],
      providers: [UserService]
    });
    service = TestBed.inject(UserService);
    httpMock = TestBed.inject(HttpTestingController);
  });

  it('debe obtener un usuario por ID', () => {
    // Arrange
    const userId = '123';
    const mockUser = { id: '123', name: 'Juan Pérez' };

    // Act
    service.getUser(userId).subscribe(user => {
      // Assert
      expect(user.id).toBe(userId);
      expect(user.name).toBe('Juan Pérez');
    });

    const req = httpMock.expectOne(\`/api/users/\${userId}\`);
    req.flush(mockUser);
  });
});
\`\`\`

Esta estructura garantiza que cada test sea claro, mantenible y fácil de entender. El bloque \`describe\` agrupa tests relacionados, \`beforeEach\` configura el entorno de prueba y cada \`it\` verifica un comportamiento específico.

## Testing de componentes con Testing Library

Para componentes, Angular Testing Library ofrece un enfoque moderno que se centra en cómo los usuarios interactúan con la aplicación:

\`\`\`typescript
import { render, screen } from '@testing-library/angular';
import { UserFormComponent } from './user-form.component';

describe('UserFormComponent', () => {
  it('debe mostrar un error cuando el email es inválido', async () => {
    await render(UserFormComponent);
    
    const emailInput = screen.getByRole('textbox', { name: /email/i });
    await user.type(emailInput, 'email-invalido');
    
    const submitButton = screen.getByRole('button', { name: /enviar/i });
    await user.click(submitButton);
    
    expect(screen.getByText(/email inválido/i)).toBeInTheDocument();
  });
});
\`\`\`

Este enfoque prueba el comportamiento desde la perspectiva del usuario, no desde los detalles de implementación. Los tests son más resistentes a cambios internos y descubren problemas de accesibilidad de forma natural.

## Mocking y dependencias

En test unitarios angular, es fundamental aislar la unidad bajo prueba. Para servicios y dependencias, Angular proporciona varias estrategias:

- **Spies**: Para métodos de servicios que necesitas verificar o controlar
- **Stubs**: Implementaciones simplificadas de dependencias complejas
- **HttpTestingController**: Para mockear llamadas HTTP sin hacer peticiones reales

El objetivo es crear un entorno controlado donde solo la unidad que estás probando pueda fallar, eliminando variables externas que compliquen el diagnóstico de errores.

## Mejores prácticas para test unitarios en Angular

**Cobertura inteligente, no obsesiva**: Busca cobertura de lógica de negocio crítica, no del 100% del código. Un test que valida un getter simple puede ser menos valioso que uno que prueba una transformación compleja de datos.

**Tests independientes**: Cada test debe poder ejecutarse en cualquier orden sin afectar a otros. Evita compartir estado entre tests más allá de lo configurado en \`beforeEach\`.

**Nombres descriptivos**: Un buen nombre de test explica qué se está probando y qué resultado se espera. \`it('debe calcular el total correctamente cuando hay descuento')\` es mejor que \`it('test 1')\`.

**Velocidad**: Los tests unitarios deben ejecutarse rápidamente. Si un test tarda más de unos segundos, probablemente estás probando demasiado o dependiendo de recursos externos.

## Integración con Jest y herramientas modernas

La migración de Karma/Jasmine a Jest ha simplificado significativamente la configuración y ejecución de test unitarios angular. Jest ofrece mejores herramientas de debugging, mejor rendimiento y una API más moderna. Combinado con Testing Library, proporciona una experiencia de testing superior que se alinea con las mejores prácticas de la industria.

Para profundizar en la implementación práctica de test unitarios angular, incluyendo configuración completa, patrones avanzados y casos de uso reales, [angular-tests.dev](https://www.angular-tests.dev/) ofrece guías completas basadas en proyectos reales. El contenido está diseñado para desarrolladores que necesitan criterio técnico real, no solo tutoriales básicos, con ejemplos aplicables inmediatamente en producción.

Los test unitarios angular no son una tarea opcional o postergable. Son una inversión en calidad de código, mantenibilidad y confianza del equipo. Cuando están bien escritos, se convierten en documentación ejecutable que explica cómo debe comportarse tu aplicación y protege contra regresiones futuras.`
  },
  {
    id: '3',
    slug: 'que-es-jest-guia-completa',
    title: '¿Qué es Jest? Guía Completa del Framework de Testing para JavaScript',
    description: 'Descubre qué es Jest, el framework de testing más popular para JavaScript y TypeScript, y cómo utilizarlo en proyectos Angular.',
    date: '2025-01-13',
    readTime: '5 min',
    keywords: ['que es jest', 'jest', 'jest angular', 'testing framework'],
    content: `¿Qué es Jest? Es un framework de testing desarrollado por Facebook que se ha convertido en el estándar de facto para probar aplicaciones JavaScript y TypeScript. Jest combina un runner de tests, una biblioteca de aserciones y herramientas de mocking en una sola solución, simplificando significativamente la configuración y ejecución de pruebas.

## Fundamentos de Jest

Jest es un framework "zero-configuration" que funciona inmediatamente después de la instalación, aunque permite personalización extensa cuando es necesario. Su diseño prioriza la simplicidad para proyectos pequeños y la flexibilidad para aplicaciones empresariales complejas.

A diferencia de frameworks anteriores como Mocha o Jasmine, Jest incluye todo lo necesario para testing: ejecución de tests, aserciones, cobertura de código, mocking y snapshots. Esta integración elimina la necesidad de configurar múltiples herramientas y reduce la fricción inicial para equipos que adoptan testing.

## Características principales de Jest

**Ejecución rápida**: Jest ejecuta tests en paralelo, optimizando el uso de CPU y reduciendo significativamente el tiempo de ejecución en proyectos grandes. Utiliza un sistema de caché inteligente que solo vuelve a ejecutar tests afectados por cambios en el código.

**Mocking integrado**: Una de las características más poderosas de Jest es su sistema de mocking. Puedes mockear módulos completos, funciones individuales o implementaciones parciales sin necesidad de librerías adicionales:

\`\`\`typescript
// Mock de un módulo completo
jest.mock('./api-service', () => ({
  fetchUser: jest.fn().mockResolvedValue({ id: 1, name: 'Test' })
}));

// Mock de una función específica
const mockFunction = jest.fn();
mockFunction.mockReturnValue(42);
\`\`\`

**Snapshots**: Los snapshots permiten capturar el estado de estructuras de datos complejas y detectar cambios inesperados. Son especialmente útiles para probar componentes de UI y estructuras de configuración:

\`\`\`typescript
it('debe renderizar el componente correctamente', () => {
  const component = render(<UserCard user={mockUser} />);
  expect(component).toMatchSnapshot();
});
\`\`\`

**Cobertura de código**: Jest incluye herramientas de cobertura integradas que generan reportes detallados sin configuración adicional. Puedes ejecutar \`jest --coverage\` y obtener métricas inmediatas sobre qué partes de tu código están siendo probadas.

## Jest en el ecosistema Angular

Aunque Angular tradicionalmente usaba Karma y Jasmine, la migración a Jest ha ganado tracción significativa. Jest ofrece mejor rendimiento, mejor experiencia de desarrollo y herramientas de debugging superiores. Para proyectos Angular modernos, especialmente aquellos que usan componentes standalone y Signals, Jest se integra perfectamente con Testing Library.

La configuración de Jest en Angular requiere algunos ajustes específicos, principalmente relacionados con el soporte de TypeScript y el manejo de módulos ES6, pero los beneficios superan ampliamente el esfuerzo inicial de configuración.

## Ventajas sobre alternativas

Comparado con Karma/Jasmine, Jest ofrece ejecución más rápida, mejor integración con herramientas modernas de desarrollo y una API más consistente. Frente a Mocha, Jest elimina la necesidad de configurar Chai para aserciones y Sinon para mocking, proporcionando todo en un solo paquete.

La comunidad y el ecosistema alrededor de Jest son masivos. Encontrarás plugins, extensiones y recursos de aprendizaje en abundancia, lo que facilita resolver problemas específicos y aprender mejores prácticas.

## Configuración básica y uso

La configuración mínima de Jest puede ser tan simple como crear un archivo \`jest.config.js\`:

\`\`\`javascript
module.exports = {
  preset: 'jest-preset-angular',
  setupFilesAfterEnv: ['<rootDir>/setup-jest.ts'],
  testMatch: ['**/*.spec.ts'],
  collectCoverageFrom: ['src/**/*.ts', '!src/**/*.spec.ts']
};
\`\`\`

Los tests en Jest siguen una estructura familiar: \`describe\` para agrupar tests relacionados, \`it\` o \`test\` para casos individuales, y \`expect\` para aserciones. La sintaxis es clara y expresiva, facilitando la lectura y mantenimiento de tests.

## Mejores prácticas con Jest

**Organización de tests**: Mantén los tests cerca del código que prueban. Un archivo \`user.service.spec.ts\` junto a \`user.service.ts\` es más mantenible que tener todos los tests en una carpeta separada.

**Tests determinísticos**: Evita dependencias de estado global, timers o fechas que puedan hacer que tests pasen o fallen de forma inconsistente. Usa \`jest.useFakeTimers()\` cuando necesites controlar el tiempo.

**Mocking estratégico**: Mockea dependencias externas (APIs, bases de datos, servicios de terceros) pero evita mockear excesivamente tu propio código. Los mocks deben aislar la unidad bajo prueba, no ocultar problemas de diseño.

Para una guía completa sobre cómo configurar Jest en Angular, integrarlo con Testing Library y aplicar estas mejores prácticas en proyectos reales, [angular-tests.dev](https://www.angular-tests.dev/) proporciona tutoriales detallados, ejemplos de código y casos de uso prácticos. El contenido está enfocado en desarrolladores que necesitan implementar testing efectivo en producción, no solo conceptos teóricos.

Jest ha transformado cómo escribimos y ejecutamos tests en JavaScript y TypeScript. Su combinación de simplicidad, potencia y herramientas integradas lo convierte en la elección natural para proyectos modernos. Entender qué es Jest y cómo aprovecharlo correctamente es fundamental para cualquier desarrollador que busque escribir código de calidad y mantenible.`
  },
  {
    id: '4',
    slug: 'testing-signals-angular',
    title: 'Testing de Signals en Angular: Guía Práctica y Ejemplos',
    description: 'Aprende cómo testear Signals en Angular moderno con Testing Library y Jest. Guía completa con ejemplos prácticos para componentes que usan Signals.',
    date: '2025-01-16',
    readTime: '6 min',
    keywords: ['testing signals angular', 'angular signals testing', 'signals unit test', 'angular moderno'],
    content: `Los Signals representan uno de los cambios más significativos en Angular moderno. Introducidos en Angular 16 y estabilizados en Angular 17+, los Signals ofrecen una forma reactiva y eficiente de manejar el estado. Sin embargo, testing de Signals en Angular requiere entender cómo funcionan y qué estrategias aplicar para probarlos correctamente.

## ¿Qué son los Signals en Angular?

Los Signals son funciones que contienen un valor y notifican a los consumidores cuando ese valor cambia. A diferencia de las propiedades tradicionales o los Observables, los Signals proporcionan una API más simple y un rendimiento optimizado para la detección de cambios.

En Angular, encuentras tres tipos principales de Signals:

- **Signal**: Un valor reactivo básico que puedes leer y escribir
- **Computed**: Un Signal derivado que se recalcula automáticamente cuando sus dependencias cambian
- **Effect**: Una función que se ejecuta cuando los Signals que lee cambian

## Desafíos al testear Signals

Testing de Signals en Angular presenta desafíos específicos. Los Signals son síncronos en su lectura pero asíncronos en su propagación de cambios. Además, los computed signals pueden tener dependencias complejas que necesitas mockear o controlar durante las pruebas.

El enfoque tradicional de testing en Angular, basado en \`ComponentFixture\` y detección de cambios manual, funciona pero no aprovecha las ventajas de Testing Library. Con Testing Library, puedes probar Signals de forma más natural, enfocándote en cómo el usuario ve los cambios en lugar de cómo Angular los propaga internamente.

## Testing de Signals básicos

Para testear un componente que usa Signals, primero necesitas entender cómo se exponen al template. Un Signal se lee en el template usando la sintaxis \`signal()\`, y Testing Library puede detectar estos cambios automáticamente:

\`\`\`typescript
import { render, screen } from '@testing-library/angular';
import { signal } from '@angular/core';
import { CounterComponent } from './counter.component';

describe('CounterComponent', () => {
  it('debe mostrar el valor inicial del signal', async () => {
    await render(CounterComponent, {
      componentInputs: {
        initialCount: signal(0)
      }
    });

    expect(screen.getByText('0')).toBeInTheDocument();
  });

  it('debe actualizar cuando el signal cambia', async () => {
    const count = signal(0);
    const { fixture } = await render(CounterComponent, {
      componentInputs: {
        count: count
      }
    });

    count.set(5);
    fixture.detectChanges();

    expect(screen.getByText('5')).toBeInTheDocument();
  });
});
\`\`\`

## Testing de Computed Signals

Los computed signals son más complejos porque dependen de otros signals. Al testearlos, debes verificar que se recalcular correctamente cuando sus dependencias cambian:

\`\`\`typescript
describe('PriceComponent', () => {
  it('debe calcular el total correctamente', async () => {
    const quantity = signal(2);
    const price = signal(10);
    
    await render(PriceComponent, {
      componentInputs: {
        quantity: quantity(),
        price: price()
      }
    });

    // El computed signal debería mostrar 20
    expect(screen.getByText(/20/)).toBeInTheDocument();

    quantity.set(3);
    // Después de detectChanges, debería mostrar 30
    // ...
  });
});
\`\`\`

## Testing de Effects

Los effects son más difíciles de testear porque ejecutan efectos secundarios. La estrategia correcta es verificar los resultados del effect, no el effect en sí:

\`\`\`typescript
it('debe guardar en localStorage cuando el signal cambia', async () => {
  const userPreferences = signal({ theme: 'light' });
  
  await render(UserPreferencesComponent, {
    componentInputs: {
      preferences: userPreferences()
    }
  });

  // Cambiar el signal
  userPreferences.set({ theme: 'dark' });
  fixture.detectChanges();

  // Verificar el efecto secundario
  expect(localStorage.getItem('userTheme')).toBe('dark');
});
\`\`\`

## Mejores prácticas para testing de Signals

**Aislar Signals en tests**: Cuando sea posible, pasa los valores de los signals como inputs en lugar de los signals mismos. Esto simplifica los tests y los hace más predecibles.

**Usar detectChanges cuando sea necesario**: Aunque Testing Library maneja muchos casos automáticamente, los cambios en signals pueden requerir llamadas explícitas a \`fixture.detectChanges()\`.

**Probar comportamiento, no implementación**: Enfócate en cómo el usuario ve los cambios reflejados en la UI, no en cómo Angular propaga los cambios internamente.

**Mockear computed signals complejos**: Si un computed signal tiene lógica compleja o dependencias externas, considera mockearlo para aislar la unidad bajo prueba.

Para profundizar en testing de Signals en Angular, incluyendo patrones avanzados, manejo de efectos y estrategias para aplicaciones complejas, [angular-tests.dev](https://www.angular-tests.dev/) ofrece guías detalladas con ejemplos reales. El contenido está diseñado para desarrolladores que trabajan con Angular moderno y necesitan implementar testing efectivo en producción.

Testing de Signals en Angular no es solo una técnica nueva, es una oportunidad para escribir tests más simples y mantenibles. Al entender cómo funcionan los Signals y cómo testearlos correctamente, puedes aprovechar al máximo las capacidades de Angular moderno mientras mantienes la confianza en tu código.`
  },
  {
    id: '5',
    slug: 'vitest-angular-alternativa-moderna-jest',
    title: 'Vitest en Angular: Alternativa Moderna y Rápida a Jest',
    description: 'Descubre Vitest, la alternativa moderna a Jest para testing en Angular. Comparación, configuración y guía de migración completa.',
    date: '2025-01-17',
    readTime: '6 min',
    keywords: ['vitest angular', 'vitest vs jest', 'angular vitest', 'vitest testing'],
    content: `Vitest se ha posicionado como una alternativa moderna y veloz a Jest para proyectos JavaScript y TypeScript. Desarrollado por el equipo de Vite, Vitest ofrece compatibilidad con la API de Jest mientras aprovecha la velocidad de Vite para ejecutar tests de forma significativamente más rápida.

## ¿Qué es Vitest?

Vitest es un framework de testing construido sobre Vite que combina la familiaridad de Jest con el rendimiento superior de Vite. A diferencia de Jest, que usa Node.js para ejecutar tests, Vitest aprovecha el sistema de módulos ESM nativo de Vite y su capacidad de transformación rápida.

La principal ventaja de Vitest es su velocidad. En proyectos grandes, Vitest puede ejecutar tests hasta 10 veces más rápido que Jest, especialmente cuando trabajas con TypeScript y módulos ES6. Esta diferencia se vuelve crítica en proyectos Angular modernos donde los tests pueden tardar minutos en ejecutarse con Jest.

## Vitest vs Jest: Comparación práctica

**Rendimiento**: Vitest es notablemente más rápido, especialmente en el modo watch. Utiliza el sistema de módulos de Vite y puede cachear transformaciones de forma más eficiente.

**Compatibilidad**: Vitest mantiene compatibilidad con la mayoría de la API de Jest, incluyendo \`describe\`, \`it\`, \`expect\` y el sistema de mocking. La migración desde Jest es relativamente sencilla.

**Configuración**: Vitest se integra naturalmente con Vite, lo que significa que si ya usas Vite en tu proyecto Angular, la configuración es más simple. Sin embargo, requiere Vite como bundler.

**Ecosistema**: Jest tiene un ecosistema más maduro y más plugins disponibles. Vitest es más nuevo pero está creciendo rápidamente.

## Configuración de Vitest en Angular

Configurar Vitest en Angular requiere algunos pasos específicos. Primero, necesitas asegurarte de que tu proyecto use Vite (o migrarlo a Vite). Luego, instalar Vitest y configurarlo:

\`\`\`bash
npm install --save-dev vitest @vitest/ui
\`\`\`

La configuración básica en \`vitest.config.ts\`:

\`\`\`typescript
import { defineConfig } from 'vitest/config';
import angular from '@analogjs/vite-plugin-angular';

export default defineConfig({
  plugins: [angular()],
  test: {
    globals: true,
    environment: 'jsdom',
    setupFiles: ['./src/test-setup.ts'],
    include: ['**/*.spec.ts'],
    coverage: {
      provider: 'v8',
      reporter: ['text', 'json', 'html']
    }
  }
});
\`\`\`

## Migración desde Jest a Vitest

Si ya tienes tests escritos con Jest, la migración a Vitest es relativamente directa:

1. **Reemplazar imports**: Cambiar imports de Jest a Vitest donde sea necesario
2. **Actualizar configuración**: Migrar la configuración de Jest a Vitest
3. **Ajustar mocks**: Algunos mocks pueden necesitar ajustes menores
4. **Verificar compatibilidad**: Asegurarte de que todos los plugins y utilidades funcionen con Vitest

La mayoría del código de tests permanece igual debido a la compatibilidad de API:

\`\`\`typescript
// Este código funciona igual en Jest y Vitest
describe('UserService', () => {
  it('debe obtener usuarios', () => {
    // ...
  });
});
\`\`\`

## Ventajas específicas para Angular

En proyectos Angular, Vitest ofrece ventajas particulares:

**Hot Module Replacement en tests**: Puedes ver cambios en tus tests reflejados instantáneamente durante el desarrollo.

**Mejor integración con Vite**: Si tu proyecto Angular usa Vite (como muchos proyectos modernos), Vitest se integra perfectamente.

**TypeScript nativo**: Vitest maneja TypeScript de forma más eficiente que Jest, reduciendo el tiempo de compilación.

**UI de testing**: Vitest incluye una interfaz web opcional para visualizar y ejecutar tests de forma interactiva.

## Cuándo usar Vitest vs Jest

**Elige Vitest si**:
- Tu proyecto ya usa Vite
- La velocidad de ejecución es crítica
- Trabajas con TypeScript y módulos ES6
- Estás empezando un proyecto nuevo

**Mantén Jest si**:
- Tu proyecto usa Webpack o Angular CLI tradicional
- Dependes de plugins específicos de Jest
- Tu equipo ya tiene experiencia profunda con Jest
- La migración sería demasiado costosa

## Mejores prácticas con Vitest

**Configurar watch mode**: Aprovecha el modo watch de Vitest para desarrollo, que es significativamente más rápido que Jest.

**Usar la UI opcional**: La interfaz web de Vitest puede mejorar significativamente la experiencia de desarrollo.

**Optimizar configuración**: Ajusta la configuración de Vitest para tu proyecto específico, especialmente los patrones de archivos y la cobertura.

Para una guía completa sobre cómo configurar Vitest en Angular, migrar desde Jest y aprovechar todas sus características, [angular-tests.dev](https://www.angular-tests.dev/) proporciona tutoriales detallados y ejemplos prácticos. El contenido incluye comparaciones detalladas, casos de uso reales y estrategias para maximizar el rendimiento.

Vitest representa el futuro del testing en proyectos modernos. Aunque Jest sigue siendo una excelente opción, Vitest ofrece ventajas significativas en velocidad y experiencia de desarrollo que pueden transformar cómo trabajas con tests en Angular.`
  },
  {
    id: '6',
    slug: 'mocking-angular-jest',
    title: 'Mocking en Angular con Jest: Guía Completa y Mejores Prácticas',
    description: 'Domina el arte del mocking en Angular con Jest. Aprende técnicas avanzadas para mockear servicios, dependencias y módulos en tus tests.',
    date: '2025-01-18',
    readTime: '7 min',
    keywords: ['mocking angular', 'jest mocking', 'angular mocks', 'jest spy'],
    content: `El mocking es una técnica fundamental en testing que permite aislar unidades de código reemplazando dependencias con implementaciones controladas. En Angular con Jest, el mocking se vuelve esencial para testear componentes y servicios de forma aislada, sin depender de APIs reales, bases de datos o servicios externos.

## ¿Qué es el Mocking?

Mocking consiste en crear objetos o funciones falsas que simulan el comportamiento de dependencias reales. Estos mocks permiten controlar las respuestas, verificar interacciones y eliminar efectos secundarios no deseados durante las pruebas.

En Angular, comúnmente necesitas mockear:
- Servicios HTTP y llamadas a APIs
- Servicios de negocio con lógica compleja
- Dependencias de terceros
- Módulos completos cuando es necesario

## Tipos de Mocks en Jest

Jest ofrece varias formas de crear mocks, cada una útil en diferentes situaciones:

**jest.fn()**: Crea una función mock que puedes espiar y controlar
**jest.mock()**: Mockea módulos completos antes de que se importen
**jest.spyOn()**: Crea un spy sobre métodos existentes de objetos
**Manual mocks**: Implementaciones personalizadas en carpetas \`__mocks__\`

## Mocking de Servicios HTTP

Una de las tareas más comunes en Angular es mockear llamadas HTTP. Aunque Angular proporciona \`HttpTestingController\`, también puedes usar mocks de Jest:

\`\`\`typescript
import { TestBed } from '@angular/core/testing';
import { HttpClientTestingModule, HttpTestingController } from '@angular/common/http/testing';
import { UserService } from './user.service';

describe('UserService', () => {
  let service: UserService;
  let httpMock: HttpTestingController;

  beforeEach(() => {
    TestBed.configureTestingModule({
      imports: [HttpClientTestingModule],
      providers: [UserService]
    });
    service = TestBed.inject(UserService);
    httpMock = TestBed.inject(HttpTestingController);
  });

  it('debe obtener usuarios', () => {
    const mockUsers = [{ id: 1, name: 'Juan' }];

    service.getUsers().subscribe(users => {
      expect(users).toEqual(mockUsers);
    });

    const req = httpMock.expectOne('/api/users');
    expect(req.request.method).toBe('GET');
    req.flush(mockUsers);
  });

  afterEach(() => {
    httpMock.verify();
  });
});
\`\`\`

## Mocking de Servicios con jest.mock()

Para servicios más complejos o cuando quieres más control, puedes usar \`jest.mock()\`:

\`\`\`typescript
// Mock del servicio antes de importar
jest.mock('./api.service', () => ({
  ApiService: jest.fn().mockImplementation(() => ({
    fetchData: jest.fn().mockResolvedValue({ data: 'test' }),
    postData: jest.fn().mockResolvedValue({ success: true })
  }))
}));

import { ComponentFixture, TestBed } from '@angular/core/testing';
import { DataComponent } from './data.component';
import { ApiService } from './api.service';

describe('DataComponent', () => {
  let component: DataComponent;
  let apiService: jest.Mocked<ApiService>;

  beforeEach(() => {
    TestBed.configureTestingModule({
      declarations: [DataComponent],
      providers: [
        { provide: ApiService, useValue: mockApiService }
      ]
    });
    // ...
  });
});
\`\`\`

## Mocking con Spies

Los spies permiten observar y controlar métodos existentes sin reemplazar completamente el objeto:

\`\`\`typescript
describe('OrderService', () => {
  let service: OrderService;
  let paymentService: PaymentService;
  let processPaymentSpy: jest.SpyInstance;

  beforeEach(() => {
    paymentService = new PaymentService();
    processPaymentSpy = jest.spyOn(paymentService, 'processPayment')
      .mockResolvedValue({ success: true, transactionId: '123' });

    TestBed.configureTestingModule({
      providers: [
        OrderService,
        { provide: PaymentService, useValue: paymentService }
      ]
    });
    service = TestBed.inject(OrderService);
  });

  it('debe procesar el pago correctamente', async () => {
    const result = await service.createOrder({ items: [] });

    expect(processPaymentSpy).toHaveBeenCalledTimes(1);
    expect(result.paymentProcessed).toBe(true);
  });
});
\`\`\`

## Mocking de Módulos Completos

A veces necesitas mockear módulos completos, especialmente cuando contienen efectos secundarios o dependencias pesadas:

\`\`\`typescript
// Mock del módulo completo
jest.mock('@angular/common/http', () => ({
  HttpClient: jest.fn(),
  HttpHeaders: jest.fn(),
  HttpParams: jest.fn()
}));

// O usando implementación manual
jest.mock('./heavy-module', () => ({
  HeavyService: jest.fn().mockImplementation(() => ({
    initialize: jest.fn(),
    process: jest.fn().mockReturnValue('processed')
  }))
}));
\`\`\`

## Mocking de Dependencias en Componentes

Al testear componentes con Testing Library, puedes mockear dependencias mediante providers:

\`\`\`typescript
import { render, screen } from '@testing-library/angular';
import { UserProfileComponent } from './user-profile.component';
import { UserService } from './user.service';

const mockUserService = {
  getCurrentUser: jest.fn().mockResolvedValue({
    id: 1,
    name: 'Test User',
    email: 'test@example.com'
  }),
  updateUser: jest.fn().mockResolvedValue({ success: true })
};

describe('UserProfileComponent', () => {
  it('debe mostrar el usuario actual', async () => {
    await render(UserProfileComponent, {
      providers: [
        { provide: UserService, useValue: mockUserService }
      ]
    });

    expect(await screen.findByText('Test User')).toBeInTheDocument();
    expect(mockUserService.getCurrentUser).toHaveBeenCalled();
  });
});
\`\`\`

## Mejores prácticas de Mocking

**Mock solo lo necesario**: No mockees todo. Mockea dependencias externas (APIs, servicios de terceros) pero evita mockear tu propio código a menos que sea absolutamente necesario.

**Usa tipos para mocks**: TypeScript puede ayudarte a mantener mocks consistentes:

\`\`\`typescript
const mockUserService: jest.Mocked<UserService> = {
  getCurrentUser: jest.fn(),
  updateUser: jest.fn()
} as any;
\`\`\`

**Verifica interacciones**: Usa \`toHaveBeenCalled\`, \`toHaveBeenCalledWith\` y \`toHaveBeenCalledTimes\` para verificar que los mocks se usan correctamente.

**Limpia mocks entre tests**: Usa \`beforeEach\` o \`afterEach\` para resetear mocks y evitar que un test afecte a otro.

**Mockea comportamientos, no datos**: Enfócate en mockear cómo se comportan las dependencias, no solo los datos que retornan.

## Errores comunes al mockear

**Mockear demasiado**: Mockear tu propio código puede ocultar problemas de diseño y hacer los tests menos valiosos.

**Mocks que no reflejan la realidad**: Asegúrate de que tus mocks se comporten de forma similar a las implementaciones reales.

**Olvidar resetear mocks**: Mocks que persisten entre tests pueden causar resultados inesperados.

**No verificar mocks**: Si mockeas algo, verifica que se use correctamente en tu test.

Para profundizar en técnicas avanzadas de mocking en Angular con Jest, incluyendo patrones complejos, manejo de async/await y estrategias para aplicaciones grandes, [angular-tests.dev](https://www.angular-tests.dev/) ofrece guías detalladas con ejemplos prácticos. El contenido está diseñado para desarrolladores que necesitan dominar el mocking para escribir tests efectivos y mantenibles.

El mocking efectivo es una habilidad que separa tests buenos de tests excelentes. Al entender cuándo y cómo mockear correctamente, puedes escribir tests que sean rápidos, confiables y que realmente protejan tu código contra regresiones.`
  },
  {
    id: '7',
    slug: 'testing-componentes-standalone-angular',
    title: 'Testing de Componentes Standalone en Angular: Guía Completa',
    description: 'Aprende a testear componentes standalone de Angular con Testing Library y Jest. Guía práctica con ejemplos y mejores prácticas.',
    date: '2025-01-19',
    readTime: '6 min',
    keywords: ['componentes standalone angular', 'standalone components testing', 'angular standalone', 'testing standalone'],
    content: `Los componentes standalone representan un cambio fundamental en cómo construimos aplicaciones Angular. Introducidos en Angular 14 y estabilizados en versiones posteriores, los componentes standalone eliminan la necesidad de módulos NgModule para muchos casos de uso. Este cambio también afecta cómo escribimos y estructuramos nuestros tests.

## ¿Qué son los Componentes Standalone?

Un componente standalone es un componente que puede existir independientemente sin necesidad de declararse en un módulo NgModule. Los componentes standalone importan directamente sus dependencias (directivas, pipes, otros componentes) mediante la propiedad \`imports\` en su decorador.

Esta arquitectura simplifica significativamente la estructura de las aplicaciones Angular y hace que los componentes sean más portables y fáciles de testear de forma aislada.

## Ventajas de testear componentes standalone

Testing de componentes standalone ofrece varias ventajas sobre el enfoque tradicional basado en módulos:

**Menos configuración**: No necesitas configurar módulos completos solo para testear un componente
**Mejor aislamiento**: Los componentes standalone son naturalmente más fáciles de aislar en tests
**Más rápido**: Menos overhead de configuración significa tests que se ejecutan más rápido
**Más claro**: La configuración de tests refleja directamente las dependencias del componente

## Configuración básica con Testing Library

Testear un componente standalone con Testing Library es más simple que con el enfoque tradicional:

\`\`\`typescript
import { render, screen } from '@testing-library/angular';
import { UserCardComponent } from './user-card.component';

describe('UserCardComponent', () => {
  it('debe renderizar el nombre del usuario', async () => {
    await render(UserCardComponent, {
      componentInputs: {
        user: {
          id: 1,
          name: 'Juan Pérez',
          email: 'juan@example.com'
        }
      }
    });

    expect(screen.getByText('Juan Pérez')).toBeInTheDocument();
  });
});
\`\`\`

Testing Library maneja automáticamente la configuración del componente standalone, incluyendo sus imports y dependencias.

## Testing con dependencias

Cuando un componente standalone tiene dependencias (otros componentes, directivas, pipes), Testing Library las maneja automáticamente. Sin embargo, puedes especificar imports adicionales si es necesario:

\`\`\`typescript
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { render, screen } from '@testing-library/angular';
import { UserFormComponent } from './user-form.component';

describe('UserFormComponent', () => {
  it('debe renderizar el formulario', async () => {
    await render(UserCardComponent, {
      imports: [
        CommonModule,
        FormsModule
      ],
      componentInputs: {
        // ...
      }
    });

    expect(screen.getByRole('form')).toBeInTheDocument();
  });
});
\`\`\`

## Testing de componentes standalone con servicios

Para componentes que inyectan servicios, puedes proporcionarlos mediante el parámetro \`providers\`:

\`\`\`typescript
import { render, screen } from '@testing-library/angular';
import { UserProfileComponent } from './user-profile.component';
import { UserService } from './user.service';

const mockUserService = {
  getCurrentUser: jest.fn().mockResolvedValue({
    id: 1,
    name: 'Test User'
  })
};

describe('UserProfileComponent', () => {
  it('debe cargar y mostrar el usuario', async () => {
    await render(UserProfileComponent, {
      providers: [
        { provide: UserService, useValue: mockUserService }
      ]
    });

    expect(await screen.findByText('Test User')).toBeInTheDocument();
    expect(mockUserService.getCurrentUser).toHaveBeenCalled();
  });
});
\`\`\`

## Testing de componentes standalone con Signals

Los componentes standalone funcionan perfectamente con Signals, y testearlos sigue siendo sencillo:

\`\`\`typescript
import { signal } from '@angular/core';
import { render, screen } from '@testing-library/angular';
import { CounterComponent } from './counter.component';

describe('CounterComponent', () => {
  it('debe mostrar el valor del signal', async () => {
    const count = signal(5);

    await render(CounterComponent, {
      componentInputs: {
        count: count()
      }
    });

    expect(screen.getByText('5')).toBeInTheDocument();
  });

  it('debe actualizar cuando el signal cambia', async () => {
    const count = signal(0);
    const { fixture } = await render(CounterComponent, {
      componentInputs: {
        count: count()
      }
    });

    count.set(10);
    fixture.detectChanges();

    expect(screen.getByText('10')).toBeInTheDocument();
  });
});
\`\`\`

## Comparación con el enfoque tradicional

El enfoque tradicional con \`TestBed\` y módulos requiere más configuración:

\`\`\`typescript
// Enfoque tradicional (más verboso)
beforeEach(() => {
  TestBed.configureTestingModule({
    declarations: [UserCardComponent],
    imports: [CommonModule],
    providers: [UserService]
  });
  fixture = TestBed.createComponent(UserCardComponent);
  component = fixture.componentInstance;
});
\`\`\`

Con componentes standalone y Testing Library, la configuración es más simple y directa, reflejando exactamente lo que el componente necesita.

## Mejores prácticas para testing de componentes standalone

**Importa solo lo necesario**: Aunque Testing Library maneja imports automáticamente, puedes especificar imports adicionales cuando sea necesario para mayor control.

**Usa componentInputs para datos**: Pasa datos al componente mediante \`componentInputs\` en lugar de modificar propiedades directamente.

**Mockea servicios externos**: Siempre mockea servicios que hacen llamadas HTTP o tienen efectos secundarios.

**Mantén tests aislados**: Cada test debe ser independiente y no depender del estado de otros tests.

**Aprovecha las queries semánticas**: Usa \`getByRole\`, \`getByLabelText\` y otras queries semánticas en lugar de selectores CSS o IDs.

## Errores comunes

**No especificar imports necesarios**: Si un componente standalone usa directivas o pipes personalizados, asegúrate de importarlos en el test.

**Olvidar detectChanges con Signals**: Cuando cambias Signals en tests, recuerda llamar \`fixture.detectChanges()\` para propagar los cambios.

**Mockear demasiado**: No mockees componentes hijos a menos que sea absolutamente necesario. Testing Library puede manejar componentes anidados.

Para una guía completa sobre testing de componentes standalone en Angular, incluyendo patrones avanzados, manejo de rutas y estrategias para aplicaciones complejas, [angular-tests.dev](https://www.angular-tests.dev/) ofrece tutoriales detallados con ejemplos prácticos. El contenido está diseñado para desarrolladores que trabajan con Angular moderno y necesitan implementar testing efectivo con componentes standalone.

Los componentes standalone han simplificado tanto el desarrollo como el testing en Angular. Al entender cómo testearlos correctamente con Testing Library, puedes escribir tests más simples, más rápidos y más mantenibles que reflejen mejor cómo los usuarios interactúan con tu aplicación.`
  }
];

export const getBlogPostBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts.find(post => post.slug === slug);
};

export const getAllBlogPosts = (): BlogPost[] => {
  return blogPosts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};
